import { client } from './ApolloClient'
import {
    REGISTER_MEMBER,
    REQUIRE_OTP,
    VERIFY_EMAIL_OTP,
    GET_MEMBER_ACCESS_TOKEN,
    REFRESH_ACCESS_TOKEN,
    REVOKE_REFRESH_TOKEN,
    GET_CURRENT_MEMBER,
    RETRIEVE_MEMBER_ORDER_HISTORY,
    RETRIEVE_MEMBER_UPCOMING_ORDERS,
    CHECK_PENDING_RATINGS,
    SUBMIT_DISH_RATINGS,
    CANCEL_MEMBER_ORDER,
    REQUEST_PASSWORD_RESET,
    VERIFY_PASSWORD_RESET_TOKEN,
    RESET_PASSWORD,
} from './schema'
import { STORAGE_KEYS, localStorage } from '../../utils/localStorage'
import { logger } from '@utils'
import ErrorTrackingAPI from '../ErrorTrackingAPI'

/**
 * Membership Services
 * handle membership related GraphQL operations
 */

/**
 * register new member with email and password
 * @param {Object} input - register data
 * @param {string} input.name - member name
 * @param {string} input.email - email address
 * @param {string} input.password - password
 * @param {string} input.phone - phone number (optional)
 * @returns {Promise<Object>} register result
 */
export const registerMember = async input => {
    try {
        const { data } = await client.mutate({
            mutation: REGISTER_MEMBER,
            variables: { input },
        })

        return {
            success: data.registerMember.result,
            error: data.registerMember.error,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'registerMember')
        return {
            success: false,
            error: error.message || 'register member failed',
        }
    }
}

/**
 * require OTP for email verification (resend)
 * @param {Object} input - OTP request data
 * @param {string} input.email - email address
 * @returns {Promise<Object>} OTP request result
 */
export const requireOTP = async input => {
    try {
        const { data } = await client.mutate({
            mutation: REQUIRE_OTP,
            variables: { input },
        })

        return {
            success: data.requireOTP.result,
            error: data.requireOTP.error,
            nextAvailableTime: data.requireOTP.nextAvailableTime || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'requireOTP')
        return {
            success: false,
            error: error.message || 'require OTP error',
            nextAvailableTime: null,
        }
    }
}

/**
 * verify email OTP
 * @param {Object} input - verification data
 * @param {string} input.email - email address
 * @param {string} input.otp - 6-digit OTP code
 * @returns {Promise<Object>} verification result
 */
export const verifyEmailOTP = async input => {
    try {
        const { data } = await client.mutate({
            mutation: VERIFY_EMAIL_OTP,
            variables: { input },
        })

        return {
            success: data.verifyEmailOTP.result,
            error: data.verifyEmailOTP.error,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'verifyEmailOTP')
        return {
            success: false,
            error: error.message || 'verify email OTP error',
        }
    }
}

/**
 * get member access token (login)
 * @param {Object} input - access token request data
 * @param {string} input.email - email address
 * @param {string} input.password - password
 * @param {boolean} input.isRememberMe - whether to remember me
 * @returns {Promise<Object>} access token result
 */
export const getMemberAccessToken = async input => {
    try {
        const { data } = await client.mutate({
            mutation: GET_MEMBER_ACCESS_TOKEN,
            variables: { input },
        })

        const { result: success, error, errorCode, accessToken, refreshToken, expiresAt } = data.getMemberAccessToken

        return {
            success,
            error,
            errorCode,
            accessToken,
            refreshToken,
            expiresAt,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'getMemberAccessToken')
        return {
            success: false,
            error: error.message || 'get member access token error',
            errorCode: null,
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
        }
    }
}

/**
 * refresh access token using refresh token
 * @param {Object} input - refresh token request data
 * @param {string} input.refreshToken - refresh token
 * @returns {Promise<Object>} refresh token result
 */
export const refreshAccessToken = async input => {
    try {
        const { data } = await client.mutate({
            mutation: REFRESH_ACCESS_TOKEN,
            variables: { input },
        })

        const { result: success, error, errorCode, accessToken, refreshToken, expiresAt } = data.refreshAccessToken

        return {
            success,
            error,
            errorCode,
            accessToken,
            refreshToken,
            expiresAt,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'refreshAccessToken')
        return {
            success: false,
            error: error.message || 'refresh access token error',
            errorCode: null,
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
        }
    }
}

/**
 * revoke refresh token (logout)
 * @param {Object} input - revoke token request data
 * @param {string} input.refreshToken - refresh token to revoke
 * @returns {Promise<Object>} revoke token result
 */
export const revokeRefreshToken = async input => {
    try {
        const { data } = await client.mutate({
            mutation: REVOKE_REFRESH_TOKEN,
            variables: { input },
        })

        return {
            success: data.revokeRefreshToken.result,
            error: data.revokeRefreshToken.error,
            errorCode: data.revokeRefreshToken.errorCode || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'revokeRefreshToken')
        return {
            success: false,
            error: error.message || 'revoke refresh token error',
            errorCode: null,
        }
    }
}

/**
 * get current member data using JWT token
 * @returns {Promise<Object>} current member data result
 */
export const getCurrentMember = async () => {
    try {
        const { data } = await client.query({
            query: GET_CURRENT_MEMBER,
            fetchPolicy: 'network-only', // Always fetch fresh data
        })

        return {
            success: data.getCurrentMember.result,
            error: data.getCurrentMember.error,
            member: data.getCurrentMember.cafeMembers[0] || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'getCurrentMember')
        return {
            success: false,
            error: error.message || 'get current member error',
            member: null,
        }
    }
}

/**
 * save tokens to localStorage
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 * @param {string} expiresAt - access token expiration time
 * @returns {boolean} whether saving was successful
 */
export const saveTokens = (accessToken, refreshToken, expiresAt) => {
    try {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
        if (refreshToken) {
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken)
        }
        if (expiresAt) {
            localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt)
        }
        return true
    } catch (error) {
        logger.error('Save tokens error:', error)
        return false
    }
}

/**
 * get tokens from localStorage
 * @returns {Object} tokens data
 */
export const getTokens = () => {
    try {
        const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)

        return {
            accessToken,
            refreshToken,
            expiresAt,
            isAccessTokenValid: accessToken && expiresAt && new Date(expiresAt) > new Date(),
            hasRefreshToken: !!refreshToken,
        }
    } catch (error) {
        logger.error('Get tokens error:', error)
        return {
            accessToken: null,
            refreshToken: null,
            expiresAt: null,
            isAccessTokenValid: false,
            hasRefreshToken: false,
        }
    }
}

/**
 * clear tokens from localStorage
 * @returns {boolean} whether clearing was successful
 */
export const clearTokens = () => {
    try {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)
        return true
    } catch (error) {
        logger.error('Clear tokens error:', error)
        return false
    }
}

/**
 * retrieve member order history
 * @param {Object} input - order history request data
 * @param {number} input.limit - number of items to retrieve
 * @param {number} input.offset - number of items to skip
 * @returns {Promise<Object>} order history result
 */
export const retrieveMemberOrderHistory = async input => {
    try {
        const { data } = await client.query({
            query: RETRIEVE_MEMBER_ORDER_HISTORY,
            variables: { input },
            fetchPolicy: 'network-only', // Always fetch fresh data
        })

        return {
            success: data.retrieveMemberOrderHistory.result,
            error: data.retrieveMemberOrderHistory.error,
            orders: data.retrieveMemberOrderHistory.orders || [],
            pageInfo: data.retrieveMemberOrderHistory.pageInfo || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'retrieveMemberOrderHistory')
        return {
            success: false,
            error: error.message || 'Retrieve member order history error',
            orders: [],
            pageInfo: null,
        }
    }
}

/**
 * retrieve member upcoming orders
 * @param {Object} input - upcoming orders request data
 * @param {number} input.limit - number of items to retrieve
 * @param {number} input.offset - number of items to skip
 * @returns {Promise<Object>} upcoming orders result
 */
export const retrieveMemberUpcomingOrders = async input => {
    try {
        const { data } = await client.query({
            query: RETRIEVE_MEMBER_UPCOMING_ORDERS,
            variables: { input },
            fetchPolicy: 'network-only', // Always fetch fresh data
        })

        return {
            success: data.retrieveMemberUpcomingOrders.result,
            error: data.retrieveMemberUpcomingOrders.error,
            orders: data.retrieveMemberUpcomingOrders.orders || [],
            pageInfo: data.retrieveMemberUpcomingOrders.pageInfo || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'retrieveMemberUpcomingOrders')
        return {
            success: false,
            error: error.message || 'Retrieve member upcoming orders error',
            orders: [],
            pageInfo: null,
        }
    }
}

/**
 * check pending ratings
 * @returns {Promise<Object>} pending ratings check result
 */
export const checkPendingRatings = async () => {
    try {
        const { data } = await client.query({
            query: CHECK_PENDING_RATINGS,
            fetchPolicy: 'network-only', // Always fetch fresh data
        })

        return {
            success: data.checkPendingRatings.result,
            error: data.checkPendingRatings.error,
            hasPendingRatings: data.checkPendingRatings.hasPendingRatings || false,
            lastOrder: data.checkPendingRatings.lastOrder || null,
            pendingItems: data.checkPendingRatings.pendingItems || [],
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'checkPendingRatings')
        return {
            success: false,
            error: error.message || 'Check pending ratings error',
            hasPendingRatings: false,
            lastOrder: null,
            pendingItems: [],
        }
    }
}

/**
 * submit dish ratings
 * @param {Object} input - ratings data
 * @param {Array} input.ratings - array of dish ratings
 * @param {number} input.ratings[].orderDishVariantId - order dish variant ID
 * @param {number} input.ratings[].score - rating score (1-5)
 * @param {string} input.ratings[].comment - optional comment
 * @returns {Promise<Object>} submit ratings result
 */
export const submitDishRatings = async input => {
    try {
        const { data } = await client.mutate({
            mutation: SUBMIT_DISH_RATINGS,
            variables: { input },
        })

        return {
            success: data.submitDishRatings.result,
            error: data.submitDishRatings.error,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'submitDishRatings')
        return {
            success: false,
            error: error.message || 'Submit dish ratings error',
        }
    }
}

/**
 * cancel member order
 * @param {Object} input - cancel order data
 * @param {number} input.orderId - order ID
 * @param {string} input.reason - optional cancellation reason
 * @returns {Promise<Object>} cancel order result
 */
export const cancelMemberOrder = async input => {
    try {
        const { data } = await client.mutate({
            mutation: CANCEL_MEMBER_ORDER,
            variables: { input },
            fetchPolicy: 'network-only',
        })

        return {
            success: data.cancelMemberOrder.result,
            error: data.cancelMemberOrder.error,
            refund: data.cancelMemberOrder.refund || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'cancelMemberOrder')
        return {
            success: false,
            error: error.message || 'Cancel member order error',
            refund: null,
        }
    }
}

/**
 * request password reset (send reset link via email)
 * @param {Object} input - reset request data
 * @param {string} input.email - email address
 * @returns {Promise<Object>} request result
 */
export const requestPasswordReset = async input => {
    try {
        const { data } = await client.mutate({
            mutation: REQUEST_PASSWORD_RESET,
            variables: { input },
        })

        return {
            success: data.requestPasswordReset.result,
            error: data.requestPasswordReset.error,
            nextAvailableTime: data.requestPasswordReset.nextAvailableTime || null,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'requestPasswordReset')
        return {
            success: false,
            error: error.message || 'Request password reset failed',
            nextAvailableTime: null,
        }
    }
}

/**
 * verify password reset token
 * @param {Object} input - verification data
 * @param {string} input.email - email address
 * @param {string} input.token - reset token
 * @returns {Promise<Object>} verification result
 */
export const verifyPasswordResetToken = async input => {
    try {
        const { data } = await client.mutate({
            mutation: VERIFY_PASSWORD_RESET_TOKEN,
            variables: { input },
        })

        return {
            success: data.verifyPasswordResetToken.result,
            error: data.verifyPasswordResetToken.error,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'verifyPasswordResetToken')
        return {
            success: false,
            error: error.message || 'Verify token failed',
        }
    }
}

/**
 * reset password with token
 * @param {Object} input - reset password data
 * @param {string} input.email - email address
 * @param {string} input.token - reset token
 * @param {string} input.newPassword - new password
 * @returns {Promise<Object>} reset result
 */
export const resetPassword = async input => {
    try {
        const { data } = await client.mutate({
            mutation: RESET_PASSWORD,
            variables: { input },
        })

        return {
            success: data.resetPassword.result,
            error: data.resetPassword.error,
        }
    } catch (error) {
        ErrorTrackingAPI.sendError(error, 'MembershipService', 'resetPassword')
        return {
            success: false,
            error: error.message || 'Reset password failed',
        }
    }
}

const membershipServices = {
    registerMember,
    requireOTP,
    verifyEmailOTP,
    getMemberAccessToken,
    refreshAccessToken,
    revokeRefreshToken,
    getCurrentMember,
    retrieveMemberOrderHistory,
    retrieveMemberUpcomingOrders,
    checkPendingRatings,
    submitDishRatings,
    cancelMemberOrder,
    requestPasswordReset,
    verifyPasswordResetToken,
    resetPassword,
    saveTokens,
    getTokens,
    clearTokens,
}

export default membershipServices
